/**
 * Grand Quack Auto — tutorial 3D markers.
 *
 * Owns the two scene objects used by tutorialSystem:
 *   - arrowGroup: 4 cardinal chevrons that float around the duck and pulse
 *     outward on a loop while the player is learning to move.
 *   - npcMarker: single down-pointing cone that appears above the nearest NPC
 *     once the duck is close enough to quack it.
 *
 * Split out of tutorialSystem.ts so that file can stay under the project's
 * 200-line cap (CLAUDE.md).
 */
import * as THREE from 'three';
import { TUTORIAL } from '../config';

let arrowGroup: THREE.Group | null = null;
let arrowMat: THREE.MeshStandardMaterial | null = null;
let arrowGeo: THREE.ConeGeometry | null = null;
let arrows: THREE.Mesh[] = [];
let npcMarker: THREE.Mesh | null = null;
let npcMarkerMat: THREE.MeshStandardMaterial | null = null;

export function ensureTutorialMarkers(scene: THREE.Scene): void {
  if (!arrowGroup) {
    const geo = new THREE.ConeGeometry(TUTORIAL.arrowSize, TUTORIAL.arrowSize * 2.2, 4);
    const mat = new THREE.MeshStandardMaterial({
      color: 0xff3ea8, emissive: 0x00e0ff, emissiveIntensity: 0.7,
      transparent: true, opacity: 0,
    });
    const group = new THREE.Group();
    const cones: THREE.Mesh[] = [];
    // 4 chevrons around duck: pivot rotated by i*90° so local +X points outward;
    // cone rotated -90° around Z so its tip (default +Y) points +X in pivot frame.
    for (let i = 0; i < 4; i++) {
      const pivot = new THREE.Object3D();
      pivot.rotation.y = (i * Math.PI) / 2;
      const cone = new THREE.Mesh(geo, mat);
      cone.rotation.z = -Math.PI / 2;
      cone.position.x = TUTORIAL.arrowOrbitRadius;
      pivot.add(cone);
      group.add(pivot);
      cones.push(cone);
    }
    group.renderOrder = 5;
    scene.add(group);
    arrowGroup = group; arrowMat = mat; arrowGeo = geo; arrows = cones;
  }
  if (!npcMarker) {
    const g = new THREE.ConeGeometry(0.35, 0.7, 4);
    g.rotateX(Math.PI); // tip points down
    const m = new THREE.MeshStandardMaterial({
      color: 0xff3ea8, emissive: 0x00e0ff, emissiveIntensity: 0.7,
      transparent: true, opacity: 0,
    });
    const mesh = new THREE.Mesh(g, m);
    mesh.renderOrder = 5; mesh.visible = false;
    scene.add(mesh);
    npcMarker = mesh; npcMarkerMat = m;
  }
}

export function setTutorialOpacity(opacity: number): void {
  if (arrowMat) arrowMat.opacity = opacity;
  if (npcMarkerMat) npcMarkerMat.opacity = opacity;
}

export function setTutorialMode(showNpc: boolean): void {
  if (arrowGroup) arrowGroup.visible = !showNpc;
  if (npcMarker) npcMarker.visible = showNpc;
}

export function updateMoveCue(duck: THREE.Object3D, elapsed: number, dt: number): void {
  if (!arrowGroup) return;
  arrowGroup.position.set(
    duck.position.x,
    duck.position.y + TUTORIAL.arrowHeight,
    duck.position.z,
  );
  arrowGroup.rotation.y += 0.5 * dt;
  const t = Math.sin(elapsed * Math.PI * 2 * TUTORIAL.arrowPulseHz) * 0.5 + 0.5;
  const radius = TUTORIAL.arrowOrbitRadius + t * TUTORIAL.arrowPulseAmount;
  for (const cone of arrows) cone.position.x = radius;
}

export function updateNpcCue(target: THREE.Object3D, elapsed: number, dt: number): void {
  if (!npcMarker) return;
  const bob = Math.sin(elapsed * TUTORIAL.markerBobFreq) * 0.15;
  npcMarker.position.set(
    target.position.x,
    target.position.y + 2.4 + bob,
    target.position.z,
  );
  npcMarker.rotation.y += TUTORIAL.markerYawSpeed * dt;
}

export function disposeTutorialMarkers(scene: THREE.Scene): void {
  if (arrowGroup) {
    scene.remove(arrowGroup);
    arrowGeo?.dispose(); arrowMat?.dispose();
    arrowGroup = null; arrowMat = null; arrowGeo = null; arrows = [];
  }
  if (npcMarker) {
    scene.remove(npcMarker);
    npcMarker.geometry.dispose(); npcMarkerMat?.dispose();
    npcMarker = null; npcMarkerMat = null;
  }
}

/** Test-only — reset module state without disposing (scene is gone). */
export function __resetTutorialMarkers(): void {
  arrowGroup = null; arrowMat = null; arrowGeo = null; arrows = [];
  npcMarker = null; npcMarkerMat = null;
}
